/**
 * UI font-size scaling (handoffs/01-design-pass.md §4).
 *
 * Scales the document root font-size, so every rem-based size in the UI
 * (Tailwind's defaults) grows/shrinks together. The chosen scale is persisted
 * to localStorage and re-applied on load, so it survives reloads.
 */

export const FONT_SCALE_KEY = 'ecointel.fontScale';
export const MIN_SCALE = 80;
export const MAX_SCALE = 140;
export const STEP = 10;
export const DEFAULT_SCALE = 100;

/** Snap to the nearest step and clamp to [MIN, MAX]; fall back to default. */
export function clampScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_SCALE;
  const snapped = Math.round(n / STEP) * STEP;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, snapped));
}

export function readFontScale() {
  try {
    const raw = window.localStorage.getItem(FONT_SCALE_KEY);
    return raw != null ? clampScale(raw) : DEFAULT_SCALE;
  } catch {
    return DEFAULT_SCALE;
  }
}

export function applyFontScale(scale) {
  const s = clampScale(scale);
  try {
    // Percentage of the browser default (usually 16px) keeps rem units honest
    // and respects a user's own browser font-size preference.
    document.documentElement.style.fontSize = `${s}%`;
  } catch {
    /* SSR / non-DOM context: no-op */
  }
  return s;
}

export function writeFontScale(scale) {
  const s = clampScale(scale);
  try {
    window.localStorage.setItem(FONT_SCALE_KEY, String(s));
  } catch {
    /* localStorage disabled (private mode): the choice just won't persist */
  }
  return s;
}

/** Read the persisted scale and apply it. Call once at app start. */
export function initFontScale() {
  return applyFontScale(readFontScale());
}
